import {
  Alert,
  Box,
  Collapse,
  Divider,
  FormControlLabel,
  IconButton,
  Link,
  Typography,
} from '@mui/material';
import {t} from 'i18next';
import {
  ChangeEvent,
  forwardRef,
  MutableRefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {ReactComponent as DropDownIcon} from '../../../../assests/svg/chev_down.svg';
import {ReactComponent as DropUpIcon} from '../../../../assests/svg/chev_up.svg';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  MAX_PASSKEY_UPLOADS,
  useEvidenceSource,
} from '../../../../hooks/upload-evidence-source/useEvidenceSource';
import AurButton from '../../../common/aur-button/AurButton';
import {AurTextField} from '../../../common/aur-text-field/AurTextField';
import AurCheckbox from '../../../common/aur-checkbox/AurCheckbox';
import AurPopup from '../../../common/aur-popup/AurPopup';
import AurSvgIcon from '../../../common/aur-svg-icon/AurSvgIcon';
import AurTooltip from '../../../common/aur-tooltip/AurTooltip';
import AurIcon, {IconNames} from '../../../common/aur-icon/AurIcon';
import {ManifestAndPassKey} from '../../../../types/upload-new-eviidence/evidenceSourceTypes';
import {useAnalytics} from '../../../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_FILE_EXTENTIONS,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../../constants/analyticsConstants';

type DeleteType = 'manifest' | 'passkey';

interface UploadButton {
  buttonLabel: string;
  label: string;
  deleteMsg?: DeleteType;
  inputRef: MutableRefObject<HTMLInputElement | null>;
  filePaths?: ManifestAndPassKey[];
  isLoading: boolean;
  isDownloadHidden: boolean;
  isMultipleFileUpload: boolean;
  tooltip?: string;
  onUploadClick?: () => void;
  onFileClick?: (e: ChangeEvent<HTMLInputElement>) => void;
  onDownloadClick?: () => void;
}

interface EssentialProcessingInstructionsProps {
  description: string;
  showManifest: boolean;
  defaultSelected?: {
    uploadedManifest?: ManifestAndPassKey;
    uploadedPasskey?: ManifestAndPassKey[];
    handlingInstructions?: string;
    isAdditionalInstructions: boolean;
  };
  showPasswordInfoIcon?: boolean;
  passwordInfoIconTooltipText?: string;
  onChange?: () => void;
}

export type EssentialProcessingInstructionsHandler = {
  getUploadedData: () => {
    manifestUploadedData?: ManifestAndPassKey;
    passkeysUploadedData?: ManifestAndPassKey[];
    instructionText?: string;
    instructionChecked: boolean;
  };
};

const EssentialProcessingInstructions = forwardRef<
  EssentialProcessingInstructionsHandler,
  EssentialProcessingInstructionsProps
>(
  (
    {
      description,
      showManifest,
      defaultSelected,
      showPasswordInfoIcon = false,
      passwordInfoIconTooltipText,
      onChange,
    },
    ref
  ) => {
    const inputUploadManifestRef = useRef<HTMLInputElement | null>(null);
    const inputUploadPasskeyRef = useRef<HTMLInputElement | null>(null);
    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const [instruction, setInstruction] = useState<string>();
    const {trackGAEvent} = useAnalytics();
    const [rememberInstructionChecked, setRememberInstructionChecked] =
      useState<boolean>(false);
    const [showDeleteAlert, setShowDeleteAlert] = useState<{
      type?: DeleteType;
      file: ManifestAndPassKey;
    }>();
    const [isShowManifestTemplate, setIsShowManifestTemplate] =
      useState<boolean>(false);

    const {
      isManifestUploading,
      isPasskeyUploading,
      manifestUploadedData,
      passkeysUploadedData,
      isManifestError,
      isPasskeyError,
      downloadManifestTemplate,
      uploadManifestFile,
      uploadPasskeyFile,
      updateManifest,
      updatePasskey,
      deleteManifestFile,
      deletePasskeyFile,
      setIsManifestError,
      setIsPasskeyError,
    } = useEvidenceSource();

    useImperativeHandle(ref, () => ({
      getUploadedData: () => {
        return {
          manifestUploadedData,
          passkeysUploadedData,
          instructionText: instruction,
          instructionChecked: rememberInstructionChecked,
        };
      },
    }));

    useEffect(() => {
      if (defaultSelected) {
        setInstruction(defaultSelected.handlingInstructions);
        setRememberInstructionChecked(defaultSelected.isAdditionalInstructions);
        updateManifest(defaultSelected.uploadedManifest);
        updatePasskey(defaultSelected.uploadedPasskey);
        if (
          defaultSelected.handlingInstructions ||
          defaultSelected.uploadedManifest ||
          defaultSelected.uploadedPasskey ||
          defaultSelected.isAdditionalInstructions
        ) {
          setIsExpanded(true);
        }
      }
    }, [defaultSelected, updateManifest, updatePasskey]);

    useEffect(() => {
      onChange?.();
    }, [
      passkeysUploadedData,
      instruction,
      rememberInstructionChecked,
      onChange,
    ]);

    const handleShowHideClick = useCallback(() => {
      setIsExpanded(!isExpanded);

      trackGAEvent({
        eventName: GA_EVENT_NAMES.CLICK,
        click: {
          name: isExpanded
            ? t('gaTracking.global.hide')
            : t('gaTracking.global.show'),
          type: GA_CLICK_TYPES.ACCORDION,
          region: GA_REGIONS.TAB,
          action: t(
            'gaTracking.uploadEvidence.processingInstructionsExpansion',
            {
              type: isExpanded
                ? t('gaTracking.global.hide')
                : t('gaTracking.global.show'),
            }
          ),
        },
        subPage: {
          subpage_name: t('gaTracking.uploadEvidence.sourceDetails'),
          subpage_type: GA_SUBPAGE_TYPE.TAB,
        },
      });
    }, [isExpanded]);

    const openManifestLibrary = useCallback(() => {
      setIsManifestError(undefined);
      inputUploadManifestRef.current?.click();
      trackGAEvent({
        eventName: GA_EVENT_NAMES.CLICK,
        click: {
          name: t('gaTracking.uploadEvidence.uploadManifest'),
          type: GA_CLICK_TYPES.ACCORDION,
          region: GA_REGIONS.TAB,
          action: t('gaTracking.uploadEvidence.uploadEvidenceAction', {
            type: t('gaTracking.uploadEvidence.uploadManifest'),
          }),
        },
        subPage: {
          subpage_name: t('gaTracking.uploadEvidence.sourceDetails'),
          subpage_type: GA_SUBPAGE_TYPE.TAB,
        },
      });
    }, [setIsManifestError]);

    const openPasskeyLibrary = useCallback(() => {
      trackGAEvent({
        eventName: GA_EVENT_NAMES.CLICK,
        click: {
          name: t('gaTracking.uploadEvidence.uploadPasskey'),
          type: GA_CLICK_TYPES.ACCORDION,
          region: GA_REGIONS.TAB,
          action: t('gaTracking.uploadEvidence.uploadEvidenceAction', {
            type: t('gaTracking.uploadEvidence.uploadPasskey'),
          }),
        },
        subPage: {
          subpage_name: t('gaTracking.uploadEvidence.sourceDetails'),
          subpage_type: GA_SUBPAGE_TYPE.TAB,
        },
      });
      setIsPasskeyError(undefined);
      inputUploadPasskeyRef.current?.click();
    }, [setIsPasskeyError]);

    const handleUploadManifest = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const selected = e.target.files ? Array.from(e.target.files) : [];
        uploadManifestFile(selected);
        if (inputUploadManifestRef.current) {
          inputUploadManifestRef.current.value = '';
        }
      },
      [uploadManifestFile]
    );

    const handleUploadPasskey = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const selected = e.target.files ? Array.from(e.target.files) : [];
        uploadPasskeyFile(selected);
        if (inputUploadPasskeyRef.current) {
          inputUploadPasskeyRef.current.value = '';
        }
      },
      [uploadPasskeyFile]
    );

    const handleDownloadManifestTemplate = useCallback(
      (isTemplate: boolean) => {
        trackGAEvent({
          eventName: GA_EVENT_NAMES.FILE_DOWNLOAD,
          file_extension: GA_FILE_EXTENTIONS.XLSX,
          click: {
            name: isTemplate
              ? t('gaTracking.uploadEvidence.downloadTemplate')
              : t(
                  'gaTracking.uploadEvidence.consilioDataDeliveryManifestAndIns'
                ),
            type: GA_CLICK_TYPES.LINK,
            region: GA_REGIONS.TAB,
            action: t(
              'gaTracking.uploadEvidence.uploadEvidenceDownloadedManifestTemplate'
            ),
          },
          subPage: {
            subpage_name: t('gaTracking.uploadEvidence.sourceDetails'),
            subpage_type: GA_SUBPAGE_TYPE.TAB,
          },
        });
        downloadManifestTemplate();
      },
      [downloadManifestTemplate]
    );

    const handleInstructionChange = useCallback(
      (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = event.target.value;
        setInstruction(value);
      },
      []
    );

    const handleRememberMeChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked;
        setRememberInstructionChecked(checked);
        trackGAEvent({
          eventName: GA_EVENT_NAMES.CLICK,
          click: {
            name: t('gaTracking.uploadEvidence.manualReviewRequired'),
            type: GA_CLICK_TYPES.CHECKBOX,
            region: GA_REGIONS.BODY,
            action: t(
              'gaTracking.uploadEvidence.uploadEvidenceCheckedManualPmReview',
              {
                checked: checked
                  ? t('gaTracking.global.checked')
                  : t('gaTracking.global.unchecked'),
              }
            ),
          },
          subPage: {
            subpage_name: t('gaTracking.uploadEvidence.sourceDetails'),
            subpage_type: GA_SUBPAGE_TYPE.TAB,
          },
        });
      },
      []
    );

    const onMenifestTemplateDialogClose = () => {
      setIsShowManifestTemplate(false);
      trackGAEvent({
        eventName: GA_EVENT_NAMES.CLICK,
        click: {
          name: t('gaTracking.global.close'),
          type: GA_CLICK_TYPES.BUTTON,
          region: GA_REGIONS.MODAL,
          action: t('gaTracking.uploadEvidence.uploadEvidenceclosedModal'),
        },
        subPage: {
          subpage_name: t('gaTracking.uploadEvidence.manifestTemplate'),
          subpage_type: GA_SUBPAGE_TYPE.MODAL,
        },
      });
    };

    const onDelete = useCallback(() => {
      trackGAEvent({
        eventName: GA_EVENT_NAMES.DELETE,
        click: {
          name: t('gaTracking.global.yes'),
          type: GA_CLICK_TYPES.BUTTON,
          region: GA_REGIONS.MODAL,
          action: t('gaTracking.uploadEvidence.uploadEvidenceDeletedAction', {
            type: showDeleteAlert?.type,
          }),
        },
        subPage: {
          subpage_name: t('gaTracking.uploadEvidence.removeThisFile'),
          subpage_type: GA_SUBPAGE_TYPE.MODAL,
        },
      });

      if (showDeleteAlert?.type === 'manifest') {
        deleteManifestFile(showDeleteAlert.file?.fileId ?? '');
      } else {
        deletePasskeyFile(showDeleteAlert?.file?.fileId ?? '');
      }

      setShowDeleteAlert(undefined);
    }, [deleteManifestFile, deletePasskeyFile, showDeleteAlert]);

    const onDeleteCancel = useCallback(() => {
      trackGAEvent({
        eventName: GA_EVENT_NAMES.CLICK,
        click: {
          name: t('gaTracking.global.close'),
          type: GA_CLICK_TYPES.BUTTON,
          region: GA_REGIONS.MODAL,
          action: t('gaTracking.uploadEvidence.uploadEvidenceclosedModal'),
        },
        subPage: {
          subpage_name: t('gaTracking.uploadEvidence.removeThisFile'),
          subpage_type: GA_SUBPAGE_TYPE.MODAL,
        },
      });
      setShowDeleteAlert(undefined);
    }, []);

    const arrowIcon = useMemo(
      () =>
        isExpanded ? (
          <DropUpIcon width={24} height={24} aria-hidden="true" />
        ) : (
          <DropDownIcon width={24} height={24} aria-hidden="true" />
        ),
      [isExpanded]
    );

    const renderUploadComponent = useCallback((props: UploadButton) => {
      const renderUploadButton = () => {
        return (
          <>
            <Box className={'upload-button-container'}>
              <AurButton
                label={props.buttonLabel}
                ariaLabel={props.buttonLabel}
                type="secondary"
                size="Medium"
                classNames="upload-button"
                showLoader={props.isLoading}
                disabled={
                  props.isMultipleFileUpload && props.filePaths
                    ? props.filePaths?.length >= MAX_PASSKEY_UPLOADS
                    : undefined
                }
                onClick={props.onUploadClick}
              />
              {props.tooltip && (
                <AurTooltip
                  title={props.tooltip}
                  aria-label={props.tooltip}
                  className="typo-text body4_size tooltop-content"
                  arrowPosition={'top'}
                  tabIndex={0}
                >
                  <IconButton className="info-icon">
                    <AurIcon icon={IconNames.Info} />
                  </IconButton>
                </AurTooltip>
              )}
            </Box>
            <input
              type="file"
              hidden
              multiple={props.isMultipleFileUpload}
              ref={props.inputRef}
              onChange={props.onFileClick}
              data-testid={`file-input-${props.label}`}
            />
          </>
        );
      };

      return (
        <Box className={'uploaded-container'}>
          {props.filePaths && props.filePaths?.length > 0 && (
            <Box className={'uploaded-container'}>
              <Box className="link-container">
                <Typography
                  tabIndex={0}
                  className="typo-text toastMessageText_size manifest-label"
                >
                  {props.label}
                  {props.isDownloadHidden && (
                    <Link
                      href="#"
                      className="typo-text body3_size link-manifest"
                      data-testid="download-template-link"
                      role="button"
                      aria-label={`${t('essentialProcessingInstructions.downloadTemplate')} for ${props.label}`}
                      onClick={(e) => {
                        e.preventDefault();
                        props.onDownloadClick?.();
                      }}
                    >
                      {t('essentialProcessingInstructions.downloadTemplate')}
                    </Link>
                  )}
                </Typography>
              </Box>
              {props.filePaths?.length > 0 &&
                props.filePaths.map((file) => {
                  return (
                    <Box key={file.fileId} className={'file-container'}>
                      <Typography
                        tabIndex={0}
                        className="typo-text body2_size doc-name-text"
                      >
                        {file?.fileName}
                      </Typography>
                      <IconButton
                        className="delete-btn"
                        aria-label={t('buttons.delete')}
                        onClick={() => {
                          setShowDeleteAlert({
                            type: props.deleteMsg,
                            file: file,
                          });
                          trackGAEvent({
                            eventName: GA_EVENT_NAMES.CLICK,
                            click: {
                              name: t('gaTracking.global.delete'),
                              type: GA_CLICK_TYPES.ICON,
                              region: GA_REGIONS.TAB,
                              action: t(
                                'gaTracking.uploadEvidence.uploadEvidenceDeleteAction',
                                {type: props.deleteMsg}
                              ),
                            },
                            subPage: {
                              subpage_name: t(
                                'gaTracking.uploadEvidence.sourceDetails'
                              ),
                              subpage_type: GA_SUBPAGE_TYPE.TAB,
                            },
                          });

                          trackGAEvent({
                            eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,

                            subPage: {
                              subpage_name: t(
                                'gaTracking.uploadEvidence.removeThisFile'
                              ),
                              subpage_type: GA_SUBPAGE_TYPE.MODAL,
                            },
                          });
                        }}
                      >
                        <DeleteOutlineIcon
                          width={24}
                          height={24}
                          className="delete-icon"
                        />
                      </IconButton>
                    </Box>
                  );
                })}
            </Box>
          )}
          {props.isMultipleFileUpload
            ? renderUploadButton()
            : !props.filePaths && renderUploadButton()}
        </Box>
      );
    }, []);

    return (
      <Box className={'essential-processing-instructions'}>
        <Box className={'processing-instructions-container'}>
          <Box className={'processing-title-container'}>
            <Typography
              tabIndex={0}
              className="typo-text subhead4_size processing-title"
            >
              {t('essentialProcessingInstructions.title')}
            </Typography>
            <Typography
              tabIndex={0}
              className="typo-text body3_size processing-description"
            >
              {description}
            </Typography>
          </Box>
          <AurButton
            classNames="expanded-button"
            label={isExpanded ? t('common.hide') : t('common.show')}
            type="secondary"
            size="Medium"
            endIcon={arrowIcon}
            onClick={handleShowHideClick}
          />
        </Box>
        <Collapse
          in={isExpanded}
          timeout="auto"
          unmountOnExit
          data-testid="collapse-essential-processing-instructions"
          id="accordion-content"
        >
          <Box className="instructions-expanded-container">
            {showManifest && (
              <>
                <Box className={'upload-manifest-container'}>
                  {renderUploadComponent({
                    buttonLabel: t(
                      'essentialProcessingInstructions.uploadManifest'
                    ),
                    label: t('essentialProcessingInstructions.manifest'),
                    deleteMsg: 'manifest',
                    inputRef: inputUploadManifestRef,
                    isLoading: isManifestUploading,
                    filePaths: manifestUploadedData && [manifestUploadedData],
                    isDownloadHidden: true,
                    isMultipleFileUpload: false,
                    onUploadClick: openManifestLibrary,
                    onFileClick: handleUploadManifest,
                    onDownloadClick: () => {
                      handleDownloadManifestTemplate(true);
                    },
                  })}
                  {!manifestUploadedData && (
                    <Box className="link-container">
                      <Box className="gradient-try-template-line" />
                      <Typography className="typo-text toastMessageText_size">
                        {t('essentialProcessingInstructions.wantToSimplify')}
                        <Link
                          href="#"
                          className="typo-text body3_size link-manifest"
                          onClick={(e) => {
                            e.preventDefault();
                            trackGAEvent({
                              eventName: GA_EVENT_NAMES.CLICK,
                              click: {
                                name: t(
                                  'gaTracking.uploadEvidence.tryOurManifestTemplate'
                                ),
                                type: GA_CLICK_TYPES.LINK,
                                region: GA_REGIONS.TAB,
                                action: t(
                                  'gaTracking.uploadEvidence.uploadEvidenceReviewTemplate'
                                ),
                              },
                              subPage: {
                                subpage_name: t(
                                  'gaTracking.uploadEvidence.sourceDetails'
                                ),
                                subpage_type: GA_SUBPAGE_TYPE.TAB,
                              },
                            });
                            trackGAEvent({
                              eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,

                              subPage: {
                                subpage_name: t(
                                  'gaTracking.uploadEvidence.manifestTemplate'
                                ),
                                subpage_type: GA_SUBPAGE_TYPE.MODAL,
                              },
                            });
                            setIsShowManifestTemplate(true);
                          }}
                        >
                          {t(
                            'essentialProcessingInstructions.tryOurManifestTemplate'
                          )}
                        </Link>
                      </Typography>
                    </Box>
                  )}
                </Box>
                {isManifestError && (
                  <Alert
                    className="alert-processing-container"
                    severity="error"
                  >
                    {isManifestError}
                  </Alert>
                )}
                <Divider className="upload-divider" />
              </>
            )}
            <Box className={'upload-passkey-container'}>
              {renderUploadComponent({
                buttonLabel: t('essentialProcessingInstructions.uploadPasskey'),
                label: t('essentialProcessingInstructions.passkey'),
                deleteMsg: 'passkey',
                inputRef: inputUploadPasskeyRef,
                filePaths: passkeysUploadedData,
                isLoading: isPasskeyUploading,
                isDownloadHidden: false,
                isMultipleFileUpload: true,
                tooltip: t('essentialProcessingInstructions.passkeyTooltip'),
                onUploadClick: openPasskeyLibrary,
                onFileClick: handleUploadPasskey,
              })}

              {showPasswordInfoIcon && !passkeysUploadedData && (
                <AurTooltip
                  title={passwordInfoIconTooltipText}
                  aria-label={passwordInfoIconTooltipText}
                  className="typo-text body4_size"
                  arrowPosition={'top'}
                  trigger="click"
                  slotProps={{
                    popper: {
                      modifiers: [
                        {
                          name: 'offset',
                          options: {
                            offset: [0, -3],
                          },
                        },
                      ],
                    },
                  }}
                >
                  <span className="info-icon">
                    <AurIcon icon={IconNames.Info} />
                  </span>
                </AurTooltip>
              )}
            </Box>
            {isPasskeyError && (
              <Alert className="alert-processing-container" severity="error">
                {isPasskeyError}
              </Alert>
            )}
            <AurTextField
              data-testid="instruction-input"
              variant="filled"
              minRows={3}
              maxRows={10}
              fullWidth
              multiline
              className="aur-textfield custom-text-field custom-textarea"
              inputProps={{
                maxLength: 2000,
              }}
              label={t(
                'essentialProcessingInstructions.specialHandlingInstructions'
              )}
              helperText={t(
                'essentialProcessingInstructions.specialHandlingInstructionsValidationMsg'
              )}
              ariaLabel={t(
                'essentialProcessingInstructions.specialHandlingInstructions'
              )}
              value={instruction}
              onChange={handleInstructionChange}
            />
            <FormControlLabel
              control={
                <AurCheckbox
                  checked={rememberInstructionChecked}
                  onChange={handleRememberMeChange}
                />
              }
              label={t('essentialProcessingInstructions.iUnderstand')}
              className="instruction-checkbox"
            />
          </Box>
        </Collapse>
        {!!showDeleteAlert && (
          <AurPopup
            className="aur-manifest-delete-popup"
            open={!!showDeleteAlert}
            popupContent={t('essentialProcessingInstructions.deleteDesc')}
            popupHeading={t('essentialProcessingInstructions.deleteTitle')}
            aria-label={t('essentialProcessingInstructions.deleteTitle')}
            showPopupHeading={true}
            showCloseIcon
            showPopupTitle
            labelActionOk={t('common.yes')}
            labelActionCancel={t('buttons.close')}
            onOk={onDelete}
            onCancel={onDeleteCancel}
          />
        )}
        {isShowManifestTemplate && (
          <AurPopup
            className="aur-manifest-download-popup"
            aria-label={t(
              'essentialProcessingInstructions.manifestModal.heading'
            )}
            closeIconHint={t('buttons.close')}
            open={isShowManifestTemplate}
            contentType="element"
            popupContent={
              <Box>
                <Typography
                  tabIndex={0}
                  className="typo-text subhead3_size heading-text"
                >
                  {t('essentialProcessingInstructions.manifestModal.heading')}
                </Typography>
                <Typography
                  tabIndex={0}
                  className="typo-text subhead2_size title-text"
                >
                  {t('essentialProcessingInstructions.manifestModal.title')}
                </Typography>
                <Typography
                  tabIndex={0}
                  className="typo-text body2_size description-text"
                >
                  {t(
                    'essentialProcessingInstructions.manifestModal.description'
                  )}
                </Typography>
                <Typography
                  tabIndex={0}
                  className="typo-text subhead4_size subtitle-text"
                >
                  {t('essentialProcessingInstructions.manifestModal.subTitle')}
                </Typography>
                <AurButton
                  classNames="download-template-button"
                  label={t(
                    'essentialProcessingInstructions.manifestModal.download'
                  )}
                  type="text"
                  size="Medium"
                  startIcon={<AurSvgIcon src="images/download.svg" />}
                  onClick={() => handleDownloadManifestTemplate(false)}
                />
              </Box>
            }
            showPopupTitle={true}
            showCloseIcon={true}
            showCancelOption={false}
            labelActionOk={t('buttons.close')}
            onOk={onMenifestTemplateDialogClose}
            onCancel={onMenifestTemplateDialogClose}
          />
        )}
      </Box>
    );
  }
);

export {EssentialProcessingInstructions};
